// INQA wizard state. Persists to localStorage so reloading mid-flow keeps the
// wizard at the same step. Mirrors the Form / current-slug split used by
// `web/public/app.js` for INQA so the same data shape can be restored.

export interface InqaForm {
  websiteUrl: string;
  projekttitel: string;
  branche: string;
  mitarbeitende: string;
  personentage: string;
  stundenProTag: string;
  zeitraumStart: string;
  zeitraumEnd: string;
  kontaktdaten: string;
  ausgangssituation: string;
  zielsetzung: string;
  kerninhalte: string;
  methoden: string;
  ergebnisse: string;
  coachName: string;
  coachUnternehmen: string;
  gestaltungsfelder: number[];
  rollen: string[];
}

/**
 * "import" = the wizard is on the new "Daten importieren" step (rendered in
 * front of the existing form/edit/download steps). The numeric `step` still
 * tracks position inside the original 3-step flow once the user has either
 * submitted an import or skipped past it.
 */
export type InqaPhase = "import" | "wizard";

export interface InqaState {
  phase: InqaPhase;
  step: 1 | 2 | 3;
  maxReachedStep: 1 | 2 | 3;
  form: InqaForm;
  currentSlug: string | null;
}

export const EMPTY_INQA_FORM: InqaForm = {
  websiteUrl: "",
  projekttitel: "",
  branche: "",
  mitarbeitende: "",
  personentage: "",
  stundenProTag: "8",
  zeitraumStart: "",
  zeitraumEnd: "",
  kontaktdaten: "",
  ausgangssituation: "",
  zielsetzung: "",
  kerninhalte: "",
  methoden: "",
  ergebnisse: "",
  coachName: "",
  coachUnternehmen: "",
  gestaltungsfelder: [],
  rollen: [],
};

export const INITIAL_INQA_STATE: InqaState = {
  phase: "import",
  step: 1,
  maxReachedStep: 1,
  form: EMPTY_INQA_FORM,
  currentSlug: null,
};

export const GESTALTUNGSFELDER: { value: number; label: string }[] = [
  { value: 1, label: "1 – Personalpolitik, Beschäftigung & Qualifizierung" },
  { value: 2, label: "2 – Sozialbeziehungen & Kultur" },
  { value: 3, label: "3 – Führung, berufliche Entwicklung & Karriere" },
  { value: 4, label: "4 – Arbeitsplatz der Zukunft, Arbeitszeit- & Leistungspolitik" },
  { value: 5, label: "5 – Neue Geschäftsmodelle, Innovationsstrategien" },
  { value: 6, label: "6 – Produktionsmodell & Arbeitsorganisation" },
];

export const GESTALTUNGSFELDER_TEXT: Record<number, string> = {
  1: "Personalpolitik, Beschäftigung & Qualifizierung",
  2: "Sozialbeziehungen & Kultur",
  3: "Führung, berufliche Entwicklung & Karriere",
  4: "Arbeitsplatz der Zukunft, Arbeitszeit- & Leistungspolitik",
  5: "Neue Geschäftsmodelle, Innovationsstrategien",
  6: "Produktionsmodell & Arbeitsorganisation",
};

const STORAGE_KEY = "zww-agent.inqa-wizard-state";

export function loadPersistedInqaState(): InqaState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return INITIAL_INQA_STATE;
    const parsed = JSON.parse(raw) as Partial<InqaState>;
    // Pre-FOR-475 saves had no `phase` field; users mid-wizard had already
    // moved past import implicitly, so default to "wizard".
    const phase: InqaPhase = parsed.phase === "import" ? "import" : "wizard";
    return {
      ...INITIAL_INQA_STATE,
      ...parsed,
      phase,
      form: { ...EMPTY_INQA_FORM, ...(parsed.form ?? {}) },
    };
  } catch {
    return INITIAL_INQA_STATE;
  }
}

export function persistInqaState(state: InqaState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore — wizard still works in-memory
  }
}

export function clearPersistedInqaState(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Build the run-directory slug used by the agent. Matches the legacy
 * `buildInqaSlug` exactly so existing output folders stay reachable.
 */
export function buildInqaSlug(titel: string): string {
  const ts = new Date().toISOString().replace(/[^0-9]/g, "").slice(0, 14);
  const slugPart = (titel || "referenz")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 40);
  return `inqa_${slugPart}_${ts}`;
}
